import dynamic from 'next/dynamic'
import { FC, Fragment, useState, useEffect } from 'react'
import { classNames } from '../../utils'
import { Popover, Transition, Tab } from '@headlessui/react'
import { Logo } from '@components/ui'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useUI } from '@components/ui'
import { Disclosure } from '@headlessui/react'

import { EmptyGuid } from '@components/utils/constants'
import AlertRibbon from '@components/ui/AlertRibbon'
import {
  BTN_SIGN_OUT,
  GENERAL_MY_ORDERS,
  MY_ACCOUNT_TITLE,
  GENERAL_WORKFLOW_TITLE,
  GENERAL_ITEM_IN_CART,
} from '@components/utils/textVariables'
import {
  getCurrentPage,
  openLoginView,
  openNewCartPage,
  removePrecedingSlash,
} from '@framework/utils/app-util'
import { IExtraProps } from '../Layout/Layout'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { recordGA4Event } from '@components/services/analytics/ga4'
import SearchWrapper from '@components/search/index'
import { CartIcon, ChevronUp, Cross } from '@components/icons'

const Account = dynamic(() => import('./AccountDropdown'))

interface Props {
  config: []
  currencies: []
  languages: []
  keywords: []
}

const NavbarAccount: FC<Props & IExtraProps> = ({
  config,
  deviceInfo,
  keywords,
  previousPath,
}: any) => {
  const { asPath } = useRouter()
  const { setSidebarView } = useUI()
  const { isMobile, isIPadorTablet } = deviceInfo
  const {
    wishListItems,
    cartItems,
    isGuestUser,
    user,
    deleteUser,
    openCart,
    displayAlert,
    showSearchBar,
    setShowSearchBar,
  } = useUI()

  const accountDropDownConfigAuthorized: any = [
    {
      href: '/my-account',
      title: MY_ACCOUNT_TITLE,
      className: 'text-left p-2 cursor-pointer',
    },
    {
      href: '/my-account/orders',
      title: GENERAL_MY_ORDERS,
      className: 'text-left p-2 cursor-pointer',
    },
    {
      href: '/',
      onClick: () => deleteUser(),
      title: BTN_SIGN_OUT,
      className: 'text-left p-2 cursor-pointer text-red-600',
    },
  ]

  let accountDropdownConfig = null
  let title = !isGuestUser
    ? user.userId
      ? `Hi, ${user.firstName}`
      : 'My account'
    : ''

  if (!isGuestUser && user.userId) {
    accountDropdownConfig = accountDropDownConfigAuthorized
  }

  const hyperlinkHandler = (hyperlink: string) => {
    return hyperlink[0] === '/' ? hyperlink : `${hyperlink}`
  }

  const [active, setActive] = useState(false)

  const handleClick = () => {
    setActive(!active)
  }

  const [open, setOpen] = useState(false)

  const [openState, setOpenState] = useState(-1)
  const [hydrationState, setHydrationState] = useState<any>(false)
  const [showHideBlack, setHideBlack] = useState(false)
  const hideBlackStrip = () => {
    setHideBlack(!showHideBlack)
  }
  let deviceCheck = ''
  if (isMobile || isIPadorTablet) {
    deviceCheck = 'Mobile'
  } else {
    deviceCheck = 'Desktop'
  }
  let currentPage = getCurrentPage()

  const viewWishlist = () => {
    if (currentPage) {
      recordGA4Event(window, GA_EVENT.WISHLIST, {
        ecommerce: {
          header: 'Menu Bar',
          current_page: currentPage,
          loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
        },
      })
    }
  }

  const navMenuLevel3GAEvent = (item: any, subHeader: any, subHeader2: any) => {
    if (typeof window !== 'undefined') {
      recordGA4Event(window, 'nav_menu', {
        category1: item,
        category2: subHeader,
        category3: subHeader2,
        previous_page: previousPath,
      })
    }
  }

  const navMenuLevel1GAEvent = (item: any) => {
    if (typeof window !== 'undefined') {
      recordGA4Event(window, 'nav_menu', {
        category1: item,
        category2: '',
        category3: '',
        previous_page: previousPath,
      })
    }
  }

  const itemsInBag = () => {
    return cartItems?.lineItems
      ?.map((item: any) => item.qty)
      .reduce((sum: number, current: number) => sum + current, 0)
  }

  const [itemsInCart, setItemsInCart] = useState(0)
  const [wishListItemsInCart, setwishListItemsInCart] = useState()
  useEffect(() => {
    setHydrationState(true)
    setItemsInCart(itemsInBag())
    setwishListItemsInCart(
      wishListItems?.length ? wishListItems?.length?.toString() : ''
    )
  }, [])

  useEffect(() => {
    setItemsInCart(itemsInBag())
    setwishListItemsInCart(
      wishListItems?.length ? wishListItems?.length?.toString() : ''
    )
  }, [cartItems, wishListItems])

  const isSearchEnable = process.env.NEXT_PUBLIC_SEARCH_ENABLED === 'true'

  return (
    <div className="sticky top-0 z-50 bg-white closeMobileNav" id="header-mob">
      <header className="relative top-0 w-full bg-white shadow">
        {isMobile && showSearchBar ? (
          <div className="absolute w-full px-0 py-3 bg-white border-b z-999">
            {isSearchEnable ? (
              <SearchWrapper
                deviceInfo={deviceInfo}
                keywords={keywords}
                previousPath={previousPath}
              />
            ) : null}
          </div>
        ) : (
          <></>
        )}
        <nav
          aria-label="Top"
          className="container hidden px-4 mx-auto lg:px-6 sm:block"
        >
          <div className="px-0 pb-0 sm:px-0 sm:pb-0">
            <div className="flex items-center justify-between h-12 sm:h-20">
              {/* Logo */}
              <button
                type="button"
                className="hidden p-3 -ml-5 text-gray-400 bg-white rounded-md sm:p-6"
                onClick={handleClick}
              >
                <span className="sr-only">Open menu</span>
                <i className="sprite-icon sprite-Menu-icon"></i>
              </button>

              <Link legacyBehavior href="/">
                <a className="hidden w-auto pr-6 cursor-pointer sm:flex sm:pr-0">
                  <span className="sr-only">{GENERAL_WORKFLOW_TITLE}</span>
                  <Logo />
                </a>
              </Link>

              {/* Flyout menus */}
              <Popover.Group className="absolute inset-x-0 bottom-0 hidden w-full sm:static sm:self-stretch lg:block sm:h-20">
                <div className="flex px-4 pb-px space-x-8 overflow-x-auto border-t h- sm:h-full sm:border-t-0 sm:justify-center sm:overflow-visible sm:pb-0">
                  {config.map((item: any, idx: number) => {
                    return (
                      <>
                        {hydrationState && (
                          <Popover
                            key={`${item.caption}${idx.toString()}`}
                            className="flex"
                            onMouseEnter={() => setOpenState(idx)}
                            onMouseLeave={() => setOpenState(-1)}
                          >
                            {({ open }) => (
                              <>
                                {hydrationState && !item.navBlocks.length ? (
                                  <Link
                                    legacyBehavior
                                    href={`/${removePrecedingSlash(
                                      item.hyperlink
                                    )}`}
                                    passHref
                                  >
                                    <a
                                      className="relative flex"
                                      onClick={() =>
                                        navMenuLevel1GAEvent(item.caption)
                                      }
                                    >
                                      <span
                                        className={classNames(
                                          idx && openState == idx
                                            ? 'border-black text-black'
                                            : 'border-transparent text-brown-light dark:text-gray-700 hover:text-gray-800',
                                          'relative z-10 flex items-center sm:h-20 transition-colors ease-out duration-200 text-xs font-medium border-b-2 -mb-px pt-px'
                                        )}
                                      >
                                        {item.caption}
                                      </span>
                                    </a>
                                  </Link>
                                ) : (
                                  hydrationState && (
                                    <span
                                      className={classNames(
                                        idx && openState == idx
                                          ? 'border-black text-black'
                                          : 'border-transparent text-brown-light dark:text-black hover:text-gray-800',
                                        'relative z-10 cursor-pointer flex items-center sm:h-20 transition-colors ease-out duration-200 text-xs font-medium border-b-2 -mb-px pt-px'
                                      )}
                                      onClick={() =>
                                        navMenuLevel1GAEvent(item.caption)
                                      }
                                    >
                                      {item.caption}
                                    </span>
                                  )
                                )}
                                <Transition
                                  show={
                                    item.navBlocks && item.navBlocks.length
                                      ? openState == idx
                                      : false
                                  }
                                  as={Fragment}
                                  enterTo="opacity-100"
                                  leaveTo="opacity-100"
                                >
                                  <Popover.Panel className="absolute inset-x-0 z-50 text-gray-500 top-full sm:text-sm">
                                    <div
                                      onMouseEnter={() => setOpenState(-1)}
                                      className="hidden popover-overlay sm:block"
                                    ></div>
                                    {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                                    <div className="relative mx-auto bg-white shadow nav-container">
                                      <div className="w-full px-4 mx-auto border-t sm:px-6 lg:px-8">
                                        <div className="flex justify-between">
                                          {item.navBlocks &&
                                            item.navBlocks
                                              .sort(
                                                (
                                                  a: { displayOrder: number },
                                                  b: { displayOrder: number }
                                                ) =>
                                                  a.displayOrder >
                                                  b.displayOrder
                                                    ? 1
                                                    : -1
                                              )
                                              .map(
                                                (
                                                  navBlock: any,
                                                  navIdx: number
                                                ) => {
                                                  return (
                                                    <div
                                                      key={`${
                                                        navBlock.boxTitle
                                                      }${navIdx.toString()}`}
                                                      className="w-full px-4 py-6"
                                                    >
                                                      <div>
                                                        <p className="mb-2 text-sm font-semibold text-gray-900 capitalize">
                                                          {navBlock.boxTitle}
                                                        </p>
                                                        <div className="w-full">
                                                          <ul
                                                            role="list"
                                                            aria-labelledby="clothing-heading"
                                                            className="w-full"
                                                          >
                                                            {navBlock.navItems
                                                              .sort(
                                                                (
                                                                  a: {
                                                                    displayOrder: number
                                                                  },
                                                                  b: {
                                                                    displayOrder: number
                                                                  }
                                                                ) =>
                                                                  a.displayOrder >
                                                                  b.displayOrder
                                                                    ? 1
                                                                    : -1
                                                              )
                                                              .map(
                                                                (
                                                                  navItem: any,
                                                                  navItemIdx: number
                                                                ) =>
                                                                  navItem?.navBlockType !=
                                                                  9 ? (
                                                                    <>
                                                                      <li
                                                                        key={`${
                                                                          navItem.caption
                                                                        }${navItemIdx.toString()}`}
                                                                        className="flex"
                                                                      >
                                                                        <Link
                                                                          href={`/men/${removePrecedingSlash(
                                                                            navItem.itemLink
                                                                          )}`}
                                                                          passHref
                                                                          legacyBehavior
                                                                        >
                                                                          <a
                                                                            onClick={() => {
                                                                              setOpenState(
                                                                                -1
                                                                              )
                                                                              navMenuLevel3GAEvent(
                                                                                item.caption,
                                                                                navBlock.boxTitle,
                                                                                navItem.caption
                                                                              )
                                                                            }}
                                                                            className="relative inline-block w-full py-4 pr-8 text-left border-b dark:text-black hover:text-gray-800 hover:border-gray-900"
                                                                          >
                                                                            <Popover.Button
                                                                              className={classNames(
                                                                                idx &&
                                                                                  openState ==
                                                                                    idx
                                                                                  ? ''
                                                                                  : 'border-transparent text-gray-dark capitalize dark:text-black hover:text-gray-800',
                                                                                'relative z-10 flex transition-colors ease-out duration-200 text-sm -mb-px pt-px text-left'
                                                                              )}
                                                                            >
                                                                              {
                                                                                navItem.caption
                                                                              }
                                                                            </Popover.Button>
                                                                            <span className="absolute right-0 top-2/4 -translate-y-2/4">
                                                                              <i className="sprite-icon sprite-nav-right-arrow"></i>
                                                                            </span>
                                                                          </a>
                                                                        </Link>
                                                                      </li>
                                                                    </>
                                                                  ) : (
                                                                    <>
                                                                      <li
                                                                        key={`${
                                                                          navItem.caption
                                                                        }${navItemIdx.toString()}`}
                                                                        className="flex"
                                                                      >
                                                                        <Link
                                                                          href={`${removePrecedingSlash(
                                                                            navItem.itemLink
                                                                          )}`}
                                                                          passHref
                                                                          legacyBehavior
                                                                        >
                                                                          <a
                                                                            onClick={() =>
                                                                              setOpenState(
                                                                                -1
                                                                              )
                                                                            }
                                                                            className="relative inline-block w-full py-4 pr-8 border-b dark:text-black hover:text-gray-800 hover:border-gray-900"
                                                                          >
                                                                            <Popover.Button
                                                                              className={classNames(
                                                                                idx &&
                                                                                  openState ==
                                                                                    idx
                                                                                  ? ''
                                                                                  : 'border-transparent text-gray-dark capitalize hover:text-gray-800 dark:text-black',
                                                                                'relative z-10 flex items-center transition-colors ease-out duration-200 text-sm -mb-px pt-px'
                                                                              )}
                                                                            >
                                                                              {
                                                                                navItem.caption
                                                                              }
                                                                            </Popover.Button>
                                                                            <span className="absolute right-0 top-2/4 -translate-y-2/4">
                                                                              <i className="sprite-icon sprite-nav-right-arrow"></i>
                                                                            </span>
                                                                          </a>
                                                                        </Link>
                                                                      </li>
                                                                    </>
                                                                  )
                                                              )}
                                                            {navBlock.contentBody !=
                                                              null ||
                                                            navBlock.contentBody !=
                                                              '' ? (
                                                              <>
                                                                <li>
                                                                  <div
                                                                    className="mt-6"
                                                                    dangerouslySetInnerHTML={{
                                                                      __html:
                                                                        navBlock.contentBody,
                                                                    }}
                                                                  ></div>
                                                                </li>
                                                              </>
                                                            ) : (
                                                              <></>
                                                            )}
                                                          </ul>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )
                                                }
                                              )}
                                        </div>
                                      </div>
                                    </div>
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        )}
                      </>
                    )
                  })}
                  <div className="flex">
                    {hydrationState && !isGuestUser && user?.userId ? (
                      <>
                        {hydrationState && (
                          <Link
                            legacyBehavior
                            href="/my-account/orders"
                            passHref
                          >
                            <span className="relative flex">
                              <span
                                className="relative z-10 flex items-center pt-px -mb-px text-xs font-medium transition-colors duration-200 ease-out border-b-2 border-transparent hover:border-black hover:border-b text-brown-light dark:text-gray-700 hover:text-gray-800 sm:h-20"
                                onClick={() =>
                                  navMenuLevel1GAEvent('Track Order')
                                }
                              >
                                Track Order
                              </span>
                            </span>
                          </Link>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          onClick={() => {
                            openLoginView(asPath)
                            setSidebarView('LOGIN_VIEW')
                          }}
                          className="cursor-pointer"
                        >
                          <span className="relative z-10 flex items-center pt-px -mb-px text-xs font-medium transition-colors duration-200 ease-out border-b-2 border-transparent hover:border-black hover:border-b text-brown-light dark:text-gray-700 hover:text-gray-800 sm:h-20">
                            Track Order
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex">
                    <Link legacyBehavior href="/stores" passHref>
                      <a
                        className="relative z-10 flex items-center pt-px -mb-px text-xs font-medium transition-colors duration-200 ease-out border-b-2 border-transparent hover:border-black hover:border-b text-brown-light dark:text-gray-700 hover:text-gray-800 sm:h-20"
                        onClick={() => navMenuLevel1GAEvent('Store Locator')}
                      >
                        Store Locator
                      </a>
                    </Link>
                  </div>
                </div>
              </Popover.Group>
              <div className="items-center justify-end flex-1 hidden sm:flex">
                {/* account */}
                <Account
                  title={title}
                  config={accountDropdownConfig}
                  deviceInfo={deviceInfo}
                  previousPath={previousPath}
                />
                {/* Wishlist*/}

                <div
                  className="flow-root w-10 px-1 sm:w-12"
                  onClick={() => viewWishlist()}
                >
                  <Link
                    legacyBehavior
                    href="/my-account/wishlist"
                    passHref
                    // onClick={openWishlist}
                  >
                    <a className="relative flex flex-col items-center justify-center grid-cols-1 mx-auto text-center group align-center">
                      <i className="sprite-icon sprite-heart"></i>
                      {/* <span className='hidden text-sm font-normal text-black sm:block'>Wishlist</span> */}
                      {wishListItemsInCart && (
                        <span
                          className="absolute w-4 h-4 ml-2 text-xs font-medium text-center text-white rounded-full -right-0 bg-orange"
                          style={{ top: '-0.3rem' }}
                        >
                          {wishListItemsInCart}
                        </span>
                      )}
                      <span className="sr-only">{GENERAL_ITEM_IN_CART}</span>
                    </a>
                  </Link>
                </div>
                {/* Cart */}

                <div className="flow-root w-10 px-1 sm:w-12">
                  <button
                    className="relative mt-1 flex flex-col items-center justify-center grid-cols-1 mx-auto text-center group align-center"
                    // onClick={openCart}
                    onClick={() => {
                      openNewCartPage(openCart, 'cart_icon')
                    }}
                  >
                    {/* <i className="sprite-icon sprite-cart" /> */}
                    <CartIcon />
                    {itemsInCart > 0 && (
                      <span
                        className="absolute w-4 h-4 ml-2 text-xs font-medium text-center text-white rounded-full bg-orange"
                        style={{ top: '-0.3rem', right: '-0.4rem' }}
                      >
                        {itemsInCart}
                      </span>
                    )}
                    <span className="sr-only">{GENERAL_ITEM_IN_CART}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <nav
        className={`${
          active ? 'navbar-mobile' : 'hidden'
        }  flex flex-wrap w-full nav-overlay lg:hidden`}
      >
        {/*Note that in this div we will use a ternary operator to decide whether or not to display the content of the div  */}
        <div
          className={`${
            active ? '' : 'hidden'
          }   w-full lg:inline-flex lg:flex-grow lg:w-auto bg-white inner-nav`}
        >
          <div className="flex flex-col items-start w-full lg:inline-flex lg:flex-row lg:ml-auto lg:w-auto lg:items-center lg:h-auto">
            {/* Links */}
            <div className="absolute flex px-4 pt-5 pb-2 right-10 r-l-32">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 -m-2 text-gray-400 rounded-md"
                onClick={handleClick}
              >
                <span className="sr-only">Close menu</span>
                <Cross className="w-6 h-6 text-black" aria-hidden="true" />
              </button>
            </div>
            <Tab.Group as="div" className="w-full mt-10">
              <div className="border-b border-gray-200 mob-scroll-sec">
                {config.map((item: any, idx: number) => {
                  return (
                    <>
                      {!item.navBlocks.length ? (
                        <Link
                          href={hyperlinkHandler(
                            removePrecedingSlash(item.hyperlink)
                          )}
                          passHref
                          legacyBehavior
                          key={idx.toString()}
                        >
                          <a
                            onClick={handleClick}
                            className="flex flex-col px-4 py-4 text-sm font-semibold uppercase border-b whitespace-nowrap dark:text-black"
                          >
                            {item.caption}
                          </a>
                        </Link>
                      ) : (
                        <>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="relative flex flex-col w-full px-4 py-4 pr-8 text-sm font-semibold uppercase border-b dark:text-black whitespace-nowrap">
                                  <span>{item.caption}</span>
                                  <ChevronUp
                                    className={`${
                                      open ? 'rotate-0 transform' : ''
                                    } h-5 w-5 text-black absolute top-2/4 right-3 -translate-y-2/4 rotate-180 transform`}
                                  />
                                </Disclosure.Button>
                                <Disclosure.Panel
                                  className="pt-2 pb-2 text-sm text-gray-500"
                                  key={`pnl${item.caption}${idx.toString()}`}
                                >
                                  <div className="space-y-4">
                                    {item.navBlocks.length ? (
                                      <div className="relative bg-white">
                                        <div className="px-0 mx-auto max-w-7xl sm:px-0 lg:px-0">
                                          <div className="grid items-start grid-cols-1 md:grid-cols-1 lg:gap-x-8">
                                            {item.navBlocks.map(
                                              (
                                                navBlock: any,
                                                navIdx: number
                                              ) => {
                                                return (
                                                  <div
                                                    key={`${navIdx.toString()}${
                                                      navBlock.boxTitle
                                                    }`}
                                                    className="grid grid-cols-1 gap-y-0 gap-x-0 lg:gap-x-0"
                                                  >
                                                    <Disclosure>
                                                      {({ open }) => (
                                                        <>
                                                          <Disclosure.Button className="relative flex flex-col px-4 py-4 pr-8 text-sm font-semibold uppercase border-b whitespace-nowrap">
                                                            <span>
                                                              {
                                                                navBlock.boxTitle
                                                              }
                                                            </span>
                                                            <ChevronUp
                                                              className={`${
                                                                open
                                                                  ? 'rotate-0 transform'
                                                                  : ''
                                                              } h-5 w-5 text-black dark:text-black absolute top-2/4 right-3 -translate-y-2/4 rotate-180 transform`}
                                                            />
                                                          </Disclosure.Button>
                                                          <Disclosure.Panel className="px-4 pt-2 pb-2 text-sm text-gray-500">
                                                            <div className="px-0 py-2 pt-2 mt-1 border-t border-gray-100 sm:grid sm:grid-cols-1 sm:gap-x-6">
                                                              <ul
                                                                role="list"
                                                                aria-labelledby="clothing-heading"
                                                                className="grid grid-cols-1"
                                                              >
                                                                {navBlock.navItems.map(
                                                                  (
                                                                    navItem: any,
                                                                    navItemIdx: number
                                                                  ) =>
                                                                    navItem?.navBlockType !=
                                                                    9 ? (
                                                                      <>
                                                                        <li
                                                                          key={`${
                                                                            navItem.caption
                                                                          }${navItemIdx.toString()}`}
                                                                          className="flex pb-2 my-1 border-b"
                                                                        >
                                                                          <Link
                                                                            legacyBehavior
                                                                            href={`/men/${removePrecedingSlash(
                                                                              navItem.itemLink
                                                                            )}`}
                                                                            passHref
                                                                          >
                                                                            <a
                                                                              onClick={() =>
                                                                                setOpen(
                                                                                  false
                                                                                )
                                                                              }
                                                                              className="text-sm hover:text-gray-800"
                                                                            >
                                                                              {
                                                                                navItem.caption
                                                                              }
                                                                            </a>
                                                                          </Link>
                                                                        </li>
                                                                      </>
                                                                    ) : (
                                                                      <>
                                                                        <li
                                                                          key={`${
                                                                            navItem.caption
                                                                          }${navItemIdx.toString()}`}
                                                                          className="flex pb-2 my-1 border-b"
                                                                        >
                                                                          <Link
                                                                            legacyBehavior
                                                                            href={`${removePrecedingSlash(
                                                                              navItem.itemLink
                                                                            )}`}
                                                                            passHref
                                                                          >
                                                                            <a
                                                                              onClick={() =>
                                                                                setOpen(
                                                                                  false
                                                                                )
                                                                              }
                                                                              className="text-sm hover:text-gray-800"
                                                                            >
                                                                              {
                                                                                navItem.caption
                                                                              }
                                                                            </a>
                                                                          </Link>
                                                                        </li>
                                                                      </>
                                                                    )
                                                                )}
                                                              </ul>
                                                            </div>
                                                          </Disclosure.Panel>
                                                        </>
                                                      )}
                                                    </Disclosure>
                                                  </div>
                                                )
                                              }
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        </>
                      )}
                    </>
                  )
                })}
              </div>
            </Tab.Group>
          </div>
        </div>
      </nav>
      {displayAlert ? (
        <div className="hidden sm:block">
          <AlertRibbon />
        </div>
      ) : null}
    </div>
  )
}
export default NavbarAccount
